import { ProductType, RecommendationType } from '@/@types';
import { UIComponent } from '@/components/core/components';
import Feed from '@/components/core/components/organism/feed';
import { useLingui } from '@lingui/react';
import clsx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FeedGroupContext } from '@/components/core/components/organism/feed/components/context/context';
import { useMediaQuery } from '@/components/core';
import { useMounted } from '@/components/core/hooks';
import { Image } from '@/components/core/components/atoms';
import { isProductOfType } from '@/services/api/parsers/utils';
import { categories } from '@/@constants';
import theme from '@/components/core/theme';
import BookTableBtn from '@/components/core/components/molecules/book-table';
import * as styles from './product-aside.styles';

const ProductAside: UIComponent<{ product: ProductType; recommendation?: RecommendationType }> = ({ product, recommendation }) => {
  const { i18n } = useLingui();
  const mediaQuery = useMediaQuery();
  const isMobile = mediaQuery.down('md');
  const mounted = useMounted();
  const videoPlayerRef = useRef<HTMLVideoElement>(null);
  const isRestaurant = isProductOfType(product, ['restaurants']);
  const [isLandscape, setIsLandscape] = useState(isRestaurant);
  const category = categories[product.category.name];

  const image =
    recommendation?.media.find((m) => {
      return m.type === 'image';
    }) ||
    product?.media.find((m) => {
      return m.type === 'image';
    });

  const video =
    recommendation?.media.find((m) => {
      return m.type === 'video';
    }) ||
    product?.media.find((m) => {
      return m.type === 'video';
    });

  const [playVideo, setPlayVideo] = useState(!!video);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!isMobile || !video) {
      return undefined;
    }
    const listener = () => {
      if (!ref.current) {
        return;
      }
      const rect = ref.current?.getBoundingClientRect();
      if (rect.top >= 0) {
        ref.current.style.setProperty('--offset-top', `calc(-1 * var(--layout-navbar))`);
      }
      ref.current.style.setProperty('--offset-top', `${-1 * rect.top}px`);
    };
    listener();
    window.addEventListener('scroll', listener);
    return () => {
      window.removeEventListener('scroll', listener);
    };
  }, [isMobile, video]);

  const playing = useMemo(() => {
    return { playing: playVideo ? product.id : null };
  }, [product.id, playVideo]);

  const imageSrc = image ? Image.getURL(image.source, theme.sizes.screens.md) : undefined;

  return (
    <aside
      ref={ref}
      className={clsx(styles.side, {
        [styles.hasVideo]: !!video,
        [styles.isLandscape]: isLandscape,
      })}
      style={
        image
          ? ({
              '--image': `url(${imageSrc})`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {!isMobile && (
        <div
          className={clsx(styles.feed, {
            [styles.hasVideo]: !!video,
          })}
        >
          <FeedGroupContext.Provider value={playing}>
            <Feed
              priority
              product={product}
              recommendation={recommendation}
              withoutOverlay
              sizes={['250px']}
              className={clsx({
                [styles.sharpBorder]: !!video,
              })}
              onClick={() => {
                setPlayVideo(!playVideo);
              }}
            >
              <Feed.Sidebar />
              <BookTableBtn className={styles.bookTableBtn} restaurant={product} />
            </Feed>
          </FeedGroupContext.Provider>
        </div>
      )}
      {isMobile && video && (
        <div className={styles.video}>
          <video
            {...(mounted && { src: video.source })}
            ref={videoPlayerRef}
            muted
            loop
            playsInline
            autoPlay
            onLoadedMetadata={() => {
              setIsLandscape((videoPlayerRef.current?.videoWidth || 1) / (videoPlayerRef.current?.videoHeight || 1) > 1);
            }}
          />
        </div>
      )}
      <div
        className={clsx(styles.imageBackground, {
          [styles.isLandscapeImage]: isLandscape,
        })}
      />
      {image && !video && (
        <Image
          src={imageSrc}
          alt={`${i18n._(category.title)} ${product.name}`}
          className={clsx(styles.image, {
            [styles.isLandscapeImage]: isLandscape,
          })}
        />
      )}
      {isMobile && (
        <>
          <Feed.Sidebar product={product} />
          <BookTableBtn className={styles.bookTableBtn} restaurant={product} />
        </>
      )}
    </aside>
  );
};

export default ProductAside;
